import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Trash2, AlertTriangle, TrendingUp, RefreshCw, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

interface TagWithStats {
  id: string;
  name: string;
  group: string;
  created_at: string;
  resourceCount: number;
  recentCount: number; // Last 30 days
}

interface DuplicateGroup {
  tags: TagWithStats[];
  similarity: number;
}

// Levenshtein distance for duplicate detection
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  const aLen = a.length;
  const bLen = b.length;

  if (aLen === 0) return bLen;
  if (bLen === 0) return aLen;

  for (let i = 0; i <= bLen; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= aLen; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= bLen; i++) {
    for (let j = 1; j <= aLen; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[bLen][aLen];
}

function similarity(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshteinDistance(a.toLowerCase(), b.toLowerCase()) / maxLen;
}

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--secondary))",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
];

export default function TagAnalytics() {
  const { organization, isLoading: orgLoading } = useOrganization();
  const [tags, setTags] = useState<TagWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "count" | "recent">("count");
  const [filterGroup, setFilterGroup] = useState<string>("all");
  const [filterUsage, setFilterUsage] = useState<"all" | "unused" | "used">("all");
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      loadAnalytics();
    }
  }, [orgLoading, organization?.id]);

  const loadAnalytics = async (forceRefresh = false) => {
    if (!organization?.id) return;
    
    setLoading(true);

    // Fetch all tags for this organization
    const { data: tagsData, error: tagsError } = await supabase
      .from("tags")
      .select("*")
      .eq("organization_id", organization.id)
      .order("name");

    if (tagsError) {
      toast.error("Failed to load tags");
      setLoading(false);
      return;
    }

    const tagIds = tagsData?.map(t => t.id) || [];
    
    // Fetch resource_tags for these tags
    const { data: resourceTags } = tagIds.length > 0 
      ? await supabase
          .from("resource_tags")
          .select("tag_id, created_at")
          .in("tag_id", tagIds)
      : { data: [] };

    // Calculate counts
    const countMap = new Map<string, number>();
    const recentCountMap = new Map<string, number>();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    resourceTags?.forEach(rt => {
      countMap.set(rt.tag_id, (countMap.get(rt.tag_id) || 0) + 1);
      if (new Date(rt.created_at) > thirtyDaysAgo) {
        recentCountMap.set(rt.tag_id, (recentCountMap.get(rt.tag_id) || 0) + 1);
      }
    });

    const tagsWithStats: TagWithStats[] = (tagsData || []).map(tag => ({
      ...tag,
      resourceCount: countMap.get(tag.id) || 0,
      recentCount: recentCountMap.get(tag.id) || 0,
    }));

    setTags(tagsWithStats);
    setLoading(false);
  };

  // Get unique groups
  const groups = useMemo(() => {
    return [...new Set(tags.map(t => t.group))].sort();
  }, [tags]);

  // Filter and sort tags
  const filteredTags = useMemo(() => {
    let result = tags;

    // Search
    if (search) {
      const query = search.toLowerCase();
      result = result.filter(t => 
        t.name.toLowerCase().includes(query) || 
        t.group.toLowerCase().includes(query)
      );
    }

    // Filter by group
    if (filterGroup !== "all") {
      result = result.filter(t => t.group === filterGroup);
    }

    // Filter by usage
    if (filterUsage === "unused") {
      result = result.filter(t => t.resourceCount === 0);
    } else if (filterUsage === "used") {
      result = result.filter(t => t.resourceCount > 0);
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "count":
          return b.resourceCount - a.resourceCount;
        case "recent":
          return b.recentCount - a.recentCount;
        default:
          return 0;
      }
    });

    return result;
  }, [tags, search, filterGroup, filterUsage, sortBy]);

  // Detect duplicates
  const duplicates = useMemo(() => {
    const groups: DuplicateGroup[] = [];
    const processed = new Set<string>();

    for (let i = 0; i < tags.length; i++) {
      if (processed.has(tags[i].id)) continue;

      const similar: TagWithStats[] = [tags[i]];
      
      for (let j = i + 1; j < tags.length; j++) {
        if (processed.has(tags[j].id)) continue;
        
        const sim = similarity(tags[i].name, tags[j].name);
        if (sim >= 0.7) { // 70% similarity threshold
          similar.push(tags[j]);
          processed.add(tags[j].id);
        }
      }

      if (similar.length > 1) {
        processed.add(tags[i].id);
        groups.push({
          tags: similar,
          similarity: Math.round(similarity(similar[0].name, similar[1].name) * 100),
        });
      }
    }

    return groups;
  }, [tags]);

  // Hot tags (most used in last 30 days)
  const hotTags = useMemo(() => {
    return [...tags]
      .filter(t => t.recentCount > 0)
      .sort((a, b) => b.recentCount - a.recentCount)
      .slice(0, 10);
  }, [tags]);

  // Chart data
  const topTagsChartData = useMemo(() => {
    return [...tags]
      .sort((a, b) => b.resourceCount - a.resourceCount)
      .slice(0, 10)
      .map(t => ({ name: t.name.length > 15 ? t.name.slice(0, 12) + "..." : t.name, count: t.resourceCount }));
  }, [tags]);

  const groupDistributionData = useMemo(() => {
    const groupCounts: Record<string, number> = {};
    tags.forEach(t => {
      groupCounts[t.group] = (groupCounts[t.group] || 0) + t.resourceCount;
    });
    return Object.entries(groupCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [tags]);

  // Stats
  const stats = useMemo(() => ({
    total: tags.length,
    unused: tags.filter(t => t.resourceCount === 0).length,
    totalUsage: tags.reduce((sum, t) => sum + t.resourceCount, 0),
    duplicateGroups: duplicates.length,
  }), [tags, duplicates]);

  const toggleSelectTag = (id: string) => {
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAllUnused = () => {
    const unusedIds = tags.filter(t => t.resourceCount === 0).map(t => t.id);
    setSelectedForDelete(new Set(unusedIds));
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    
    try {
      // Delete resource_tags associations first (shouldn't exist for unused, but just in case)
      await supabase
        .from("resource_tags")
        .delete()
        .in("tag_id", Array.from(selectedForDelete));

      // Delete tags
      const { error } = await supabase
        .from("tags")
        .delete()
        .in("id", Array.from(selectedForDelete));

      if (error) throw error;

      toast.success(`Deleted ${selectedForDelete.size} tag(s)`);
      setSelectedForDelete(new Set());
      await loadAnalytics(true);
    } catch (error: any) {
      toast.error("Failed to delete tags: " + error.message);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-[300px]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/library/tags">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-display">Tag Analytics</h1>
            <p className="text-muted-foreground">Usage statistics and insights</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => loadAnalytics(true)} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className={stats.unused > 0 ? "border-amber-500/50" : ""}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Unused Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.unused}</p>
            {stats.unused > 0 && (
              <p className="text-xs text-amber-500 mt-1">Consider cleanup</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.totalUsage}</p>
          </CardContent>
        </Card>
        <Card className={stats.duplicateGroups > 0 ? "border-amber-500/50" : ""}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Potential Duplicates</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.duplicateGroups}</p>
            {stats.duplicateGroups > 0 && (
              <p className="text-xs text-amber-500 mt-1">Review below</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Tags Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top 10 Tags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topTagsChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis dataKey="name" type="category" width={100} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: "hsl(var(--card))", 
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px"
                  }} 
                />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Group Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Usage by Group</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={groupDistributionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {groupDistributionData.map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: "hsl(var(--card))", 
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px"
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Hot Tags */}
      {hotTags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              Hot Tags (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {hotTags.map(tag => (
                <Badge key={tag.id} variant="secondary" className="gap-2">
                  {tag.name}
                  <span className="text-xs bg-orange-500/20 text-orange-500 px-1.5 py-0.5 rounded">
                    +{tag.recentCount}
                  </span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Duplicate Detection */}
      {duplicates.length > 0 && (
        <Card className="border-amber-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Potential Duplicates Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {duplicates.map((group, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg">
                  <span className="text-xs text-amber-500">{group.similarity}% similar:</span>
                  {group.tags.map(tag => (
                    <Badge key={tag.id} variant="outline">
                      {tag.name} ({tag.resourceCount})
                    </Badge>
                  ))}
                  <Link to="/admin/library/tags" className="ml-auto">
                    <Button variant="ghost" size="sm">Merge</Button>
                  </Link>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tags Table with Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle>All Tags</CardTitle>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tags..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              <Select value={filterGroup} onValueChange={setFilterGroup}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All groups" />
                </SelectTrigger>
                <SelectContent className="bg-popover border border-border z-50">
                  <SelectItem value="all">All Groups</SelectItem>
                  {groups.map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterUsage} onValueChange={(v: any) => setFilterUsage(v)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border border-border z-50">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="used">Used</SelectItem>
                  <SelectItem value="unused">Unused</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border border-border z-50">
                  <SelectItem value="count">By Usage</SelectItem>
                  <SelectItem value="recent">By Recent</SelectItem>
                  <SelectItem value="name">By Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions */}
          {selectedForDelete.size > 0 && (
            <div className="flex items-center gap-4 mb-4 p-3 bg-destructive/10 rounded-lg">
              <span className="text-sm">{selectedForDelete.size} tag(s) selected</span>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedForDelete(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          )}

          {stats.unused > 0 && selectedForDelete.size === 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={selectAllUnused}
              className="mb-4 gap-2"
            >
              Select All Unused ({stats.unused})
            </Button>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Group</TableHead>
                <TableHead className="text-center">Usage</TableHead>
                <TableHead className="text-center">Recent (30d)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTags.map(tag => (
                <TableRow key={tag.id} className={tag.resourceCount === 0 ? "opacity-60" : ""}>
                  <TableCell>
                    <Checkbox
                      checked={selectedForDelete.has(tag.id)}
                      onCheckedChange={() => toggleSelectTag(tag.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{tag.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{tag.group}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={tag.resourceCount === 0 ? "secondary" : "default"}>
                      {tag.resourceCount}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {tag.recentCount > 0 ? (
                      <Badge className="bg-orange-500/20 text-orange-500">+{tag.recentCount}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredTags.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No tags found matching your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete {selectedForDelete.size} Tag(s)
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete these tags? This action cannot be undone.
              Any resources using these tags will be untagged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
